import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination
} from '@mui/material';
import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    AddCircle as AddCircleIcon,
    List as ListIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

function Zones() {
    const location = useLocation();
    const navigate = useNavigate();
    const [zones, setZones] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        company_id: '',
        location: '',
        status: 'active'
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [allZones, setAllZones] = useState([]);
    const [filteredZones, setFilteredZones] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedZone, setSelectedZone] = useState(null);
    const user = JSON.parse(localStorage.getItem('user'));
    const isAdmin = user?.role === 'admin';

    const company = location.state?.company;

    // Add pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [displayedZones, setDisplayedZones] = useState([]);

    useEffect(() => {
        const initializeData = async () => {
            try {
                setLoading(true);
                setError(null);
                await fetchCompanies();
                await fetchZones();
            } catch (err) {
                setError('Failed to load data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, [company?.id]); // Only re-run when company ID changes

    const fetchCompanies = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/companies/read.php`);
            if (response.data && response.data.records) {
                setCompanies(response.data.records);
            }
        } catch (err) {
            throw new Error('Failed to fetch companies');
        }
    };

    const fetchZones = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/zones/read.php`);
            if (response.data && response.data.records) {
                const allZones = response.data.records;
                setAllZones(allZones);
                
                // If company is selected, filter zones for that company
                if (company?.id) {
                    const companyZones = allZones.filter(zone => zone.company_id === company.id);
                    setFilteredZones(companyZones);
                } else {
                    setFilteredZones(allZones);
                }
            }
        } catch (err) {
            throw new Error('Failed to fetch zones');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (zone = null) => {
        if (zone) {
            setIsEditing(true);
            setSelectedZone(zone);
            setFormData({
                name: zone.name,
                description: zone.description || '',
                company_id: zone.company_id,
                location: zone.location || '',
                status: zone.status || 'active'
            });
        } else {
            setIsEditing(false);
            setSelectedZone(null);
            setFormData({
                name: '',
                description: '',
                company_id: company ? company.id : '',
                location: '',
                status: 'active'
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setIsEditing(false);
        setSelectedZone(null);
        setFormData({
            name: '',
            description: '',
            company_id: '',
            location: '',
            status: 'active'
        });
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let response;
            if (isEditing && selectedZone) {
                // Update existing zone
                response = await axios.post(`${API_BASE_URL}/zones/update.php`, {
                    id: selectedZone.id,
                    name: formData.name,
                    description: formData.description,
                    company_id: formData.company_id,
                    location: formData.location,
                    status: formData.status
                });
                setSuccess('Zone updated successfully');
            } else {
                // Create new zone
                response = await axios.post(`${API_BASE_URL}/zones/create.php`, formData);
                setSuccess('Zone created successfully');
            }
            
            handleCloseDialog();
            fetchZones();
        } catch (err) {
            setError(err.response?.data?.message || `Error ${isEditing ? 'updating' : 'creating'} zone`);
        }
    };

    const handleToggleZoneStatus = async (zone) => {
        try {
            const newStatus = zone.status === 'active' ? 'closed' : 'active';
            const response = await axios.put(`${API_BASE_URL}/zones/update.php`, {
                id: zone.id,
                name: zone.name,
                description: zone.description,
                company_id: zone.company_id,
                status: newStatus
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && response.data.message) {
                setSuccess(`Zone ${newStatus === 'active' ? 'opened' : 'closed'} successfully`);
                fetchZones();
            } else {
                setError('Error updating zone status');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error updating zone status');
        }
    };

    const getCompanyName = (companyId) => {
        const company = companies.find(c => c.id === companyId);
        return company ? company.name : 'Unknown Company';
    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);

        if (!value.trim()) {
            // If no search term, show all zones or company-specific zones
            if (company) {
                const companyZones = allZones.filter(zone => zone.company_id === company.id);
                setFilteredZones(companyZones);
            } else {
                setFilteredZones(allZones);
            }
            return;
        }

        // Filter based on search term and company (if selected)
        const filtered = allZones.filter(zone => {
            const matchesSearch = 
                zone.name.toLowerCase().includes(value) ||
                zone.location.toLowerCase().includes(value) ||
                (zone.description && zone.description.toLowerCase().includes(value));
            
            // If company is selected, also filter by company_id
            if (company) {
                return matchesSearch && zone.company_id === company.id;
            }
            return matchesSearch;
        });

        setFilteredZones(filtered);
    };

    const handleDelete = async (zoneId) => {
        if (!window.confirm('Are you sure you want to delete this zone? This will also delete all assets in this zone.')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/zones/delete.php`, {
                data: { id: zoneId }
            });
            setSuccess('Zone deleted successfully');
            fetchZones();
        } catch (err) {
            setError(err.response?.data?.message || 'Error deleting zone');
        }
    };

    // Add pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Update displayed zones when pagination or filtered zones change
    useEffect(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        setDisplayedZones(filteredZones.slice(startIndex, endIndex));
    }, [filteredZones, page, rowsPerPage]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: { xs: 2, sm: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {company && (
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/companies')}
                                sx={{ mr: 2 }}
                            >
                                Back to Companies
                            </Button>
                        )}
                        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            Zones {company && `- ${company.name}`}
                        </Typography>
                    </Box>
                    {/* Allow all users to add zones */}
                    {company && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                            fullWidth={false}
                        >
                            Add Zone
                        </Button>
                    )}
                </Box>

                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search zones..."
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                            ),
                        }}
                    />
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Mobile View */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {displayedZones.map((zone) => (
                        <Paper
                            key={zone.id}
                            sx={{
                                p: 2,
                                mb: 2,
                                borderRadius: 2,
                                '&:hover': {
                                    boxShadow: 3,
                                    cursor: 'pointer'
                                }
                            }}
                            onClick={() => navigate('/assets', {
                                state: {
                                    zone,
                                    company: companies.find(c => c.id === zone.company_id),
                                    viewOnly: !isAdmin
                                }
                            })}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                    {zone.name}
                                </Typography>
                                <Chip
                                    label={zone.status === 'active' ? 'Open' : 'Closed'}
                                    color={zone.status === 'active' ? 'success' : 'error'}
                                    size="small"
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {getCompanyName(zone.company_id)}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Location: {zone.location}
                            </Typography>
                            {zone.description && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {zone.description}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                {isAdmin && company && (
                                    <>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenDialog(zone);
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(zone.id);
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </>
                                )}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleZoneStatus(zone);
                                    }}
                                >
                                    {zone.status === 'active' ? 'Close Zone' : 'Open Zone'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ListIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate('/assets', {
                                            state: {
                                                zone,
                                                company: companies.find(c => c.id === zone.company_id),
                                                viewOnly: !isAdmin
                                            }
                                        });
                                    }}
                                >
                                    View Assets
                                </Button>
                            </Box>
                        </Paper>
                    ))}
                </Box>

                {/* Desktop View */}
                <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedZones.map((zone) => (
                                <TableRow 
                                    key={zone.id}
                                    onClick={() => navigate('/assets', {
                                        state: {
                                            zone,
                                            company: companies.find(c => c.id === zone.company_id),
                                            viewOnly: !isAdmin
                                        }
                                    })}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                >
                                    <TableCell>{zone.name}</TableCell>
                                    <TableCell>{zone.location}</TableCell>
                                    <TableCell>{getCompanyName(zone.company_id)}</TableCell>
                                    <TableCell>{zone.description}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={zone.status === 'active' ? 'Open' : 'Closed'}
                                            color={zone.status === 'active' ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {isAdmin && company && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDialog(zone);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(zone.id);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleZoneStatus(zone);
                                                }}
                                            >
                                                {zone.status === 'active' ? 'Close Zone' : 'Open Zone'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<ListIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate('/assets', {
                                                        state: {
                                                            zone,
                                                            company: companies.find(c => c.id === zone.company_id),
                                                            viewOnly: !isAdmin
                                                        }
                                                    });
                                                }}
                                            >
                                                View Assets
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={filteredZones.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </TableContainer>

                {/* Mobile Pagination */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mt: 2 }}>
                    <TablePagination
                        component="div"
                        count={filteredZones.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{
                            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                margin: 0
                            }
                        }}
                    />
                </Box>

                {/* Zone Creation/Edit Dialog */}
                <Dialog 
                    open={openDialog} 
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            {isEditing ? (isAdmin ? 'Edit Zone' : 'View Zone') : 'Add New Zone'}
                            <IconButton onClick={handleCloseDialog} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Zone Name"
                                        name="name"
                                        id="zone-name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        margin="normal"
                                        disabled={isEditing && !isAdmin}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        id="zone-description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={3}
                                        margin="normal"
                                        disabled={isEditing && !isAdmin}
                                    />
                                </Grid>
                                {!company && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth required margin="normal">
                                            <InputLabel id="company-select-label">Company</InputLabel>
                                            <Select
                                                name="company_id"
                                                id="company-select"
                                                value={formData.company_id}
                                                onChange={handleInputChange}
                                                label="Company"
                                                labelId="company-select-label"
                                                disabled={isEditing && !isAdmin}
                                            >
                                                {companies.map((company) => (
                                                    <MenuItem key={company.id} value={company.id}>
                                                        {company.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <FormControl fullWidth required margin="normal">
                                        <InputLabel id="location-type-label">Location Type</InputLabel>
                                        <Select
                                            name="location"
                                            id="location-type-select"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            label="Location Type"
                                            labelId="location-type-label"
                                            disabled={isEditing && !isAdmin}
                                        >
                                            <MenuItem value="Kitchen">Kitchen</MenuItem>
                                            <MenuItem value="Market">Market</MenuItem>
                                            <MenuItem value="Warehouse">Warehouse</MenuItem>
                                            <MenuItem value="Office">Office</MenuItem>
                                            <MenuItem value="Production">Production</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {/* Only show status field for admin users */}
                                {isAdmin && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="status-select-label">Status</InputLabel>
                                            <Select
                                                name="status"
                                                id="status-select"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                                label="Status"
                                                labelId="status-select-label"
                                            >
                                                <MenuItem value="active">Active</MenuItem>
                                                <MenuItem value="closed">Closed</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        {/* Show create/update button for new zones or admin users */}
                        {(!isEditing || isAdmin) && (
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={!formData.name || !formData.company_id || !formData.location || (isEditing && !isAdmin)}
                            >
                                {isEditing ? 'Update' : 'Create'}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
}

export default Zones; 